import { useState } from 'react';
import { Button, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { PageHeader } from '../../../../shared/components/PageHeader';
import { DataTable, type DataTableQuery } from '../../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../../shared/grid/usePersistedGridState';
import { IncludeInactiveToggle } from '../../../../shared/components/IncludeInactiveToggle';
import { ConfirmDialog } from '../../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../../shared/components/ErrorState';
import { useToast } from '../../../../shared/stores/toast.store';
import { describeApiError } from '../../../../shared/api/errors';
import { feriadosHooks } from '../hooks';
import { feriadosApi } from '../api';
import { FeriadoFormDialog } from '../FeriadoFormDialog';
import { FERIADO_TIPOS, type FeriadoDto, type FeriadoListItem } from '../types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'data',
  sortOrder: 'desc',
};

type Row = FeriadoListItem & { id: string };

const TIPO_LABEL: Record<string, string> = Object.fromEntries(
  FERIADO_TIPOS.map((t) => [t.value, t.label]),
);

export function FeriadosListPage(): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { query, setQuery, clear } = usePersistedGridState('feriados', DEFAULT_QUERY, {});
  const [editing, setEditing] = useState<FeriadoDto | null | 'new'>(null);
  const [pendingDelete, setPendingDelete] = useState<Row | null>(null);

  const listQuery = feriadosHooks.useList(query);
  const removeMutation = feriadosHooks.useDelete();

  async function openEdit(row: Row): Promise<void> {
    try {
      const detail = await feriadosApi.get(row.data);
      setEditing(detail);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await removeMutation.mutateAsync(pendingDelete.data);
      toast.success(t('features.feriados.toasts.deleted'));
      setPendingDelete(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const columns: GridColDef<Row>[] = [
    {
      field: 'data',
      headerName: t('features.feriados.columns.data'),
      width: 130,
      valueFormatter: (value: string) => dayjs(value).format('DD/MM/YYYY'),
    },
    { field: 'nome', headerName: t('features.feriados.columns.nome'), flex: 1, minWidth: 220 },
    {
      field: 'tipo',
      headerName: t('features.feriados.columns.tipo'),
      width: 120,
      valueFormatter: (value: string) => TIPO_LABEL[value] ?? value,
    },
    {
      field: 'ativo',
      headerName: t('features.feriados.columns.ativo'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={
            params.value
              ? t('features.feriados.status.active')
              : t('features.feriados.status.inactive')
          }
          color={params.value ? 'success' : 'default'}
          size="small"
          variant={params.value ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('common.edit')}>
            <IconButton
              size="small"
              aria-label={t('features.feriados.actions.edit', { nome: params.row.nome })}
              onClick={() => void openEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton
              size="small"
              color="error"
              aria-label={t('features.feriados.actions.delete', { nome: params.row.nome })}
              onClick={() => setPendingDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <>
        <PageHeader title={t('menu.holidays')} />
        <ErrorState
          description={describeApiError(listQuery.error)}
          onRetry={() => listQuery.refetch()}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('menu.holidays')}
        breadcrumbs={[{ label: t('menu.generalRegistries') }, { label: t('menu.holidays') }]}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
            {t('features.feriados.actions.new')}
          </Button>
        }
      />

      <DataTable<Row>
        rows={listQuery.data?.data ?? []}
        columns={columns}
        total={listQuery.data?.meta.total ?? 0}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        onClear={clear}
        searchPlaceholder={t('features.feriados.searchPlaceholder')}
        filters={<IncludeInactiveToggle query={query} onChange={setQuery} />}
      />

      <FeriadoFormDialog
        open={editing !== null}
        feriado={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('features.feriados.deleteConfirm.title')}
        description={t('features.feriados.deleteConfirm.description', {
          nome: pendingDelete?.nome ?? '',
        })}
        confirmLabel={t('common.delete')}
        loading={removeMutation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
